// @ts-strict-ignore
import { inject, Injector } from '@angular/core';
import { catchError, forkJoin, map, Observable, of, Subject, tap } from 'rxjs';
import { ActionOriginResponse } from '../../../api/core/models/api/action-origin-response';
import { ActionStatusResponse } from '../../../api/core/models/api/action-status-response';
import { ActionTagResponse } from '../../../api/core/models/api/action-tag-response';
import { ActionTypeResponse } from '../../../api/core/models/api/action-type-response';
import { LinkingImageResponse } from '../../../api/core/models/api/linking-image-response';
import { StakeholderResponse } from '../../../api/core/models/api/stakeholder-response';
import { findDifferentProperties } from '../../../functions/utilities/find-different-properties';
import { ClientBoard } from '../../../model/client-model/client-board';
import { ActionOrigin } from '../../../model/client-model/properties/action-origin';
import { ActionsFacadeService } from '../../../store/actions/actions-facade.service';
import { RecursiveKeyOf } from '../../../store/board/board.actions';
import { MultiyearUpdateValuesService } from '../../../store/inventories/utils/multiyear-update-values.service';
import { ACTION_STATUS, ClientBoardV2 } from '../../backend-service/model';
import { ACTION_STATUS_ALLOWED } from '../../backend-service/model/action-status';
import { STAKEHOLDER_TYPE_ALLOWED } from '../../backend-service/model/stakeholder-type';
import { BackendBoardsV2Service } from '../../backend/backend-boards-v2.service';
import { InventoriesFacadeService } from '../inventories/inventories-facade.service';
import { IBoardSaveManagerService } from './board-save-manager.abstract';
import {
  distinguishChangedItems,
  findMovedItemId,
  isValidStakeholder,
  validateCreatedAndUpdatedStakeholders,
} from './board-save-manager.functions';
import { BoardRequests, ExecuteBoardRequests, QueueItem } from './board-save-manager.types';

export class BoardSaveManagerService implements IBoardSaveManagerService {
  private _injector = inject(Injector);

  private _currentlySaving: Set<string> = new Set<string>();
  private _saveQueue: Map<string, QueueItem> = new Map();
  private _observables: Map<string, Subject<ClientBoard>> = new Map();
  private _propertyActions = {
    board: {
      paths: [['title'], ['isPrimary']],
      action: () => (board: ClientBoardV2) => this._injector.get(BackendBoardsV2Service).updateBoard(board),
    },
    mainStory: {
      paths: [
        ['boardSettings', 'rootNodeProperties', 'diagramHeader'],
        ['boardSettings', 'extendedDescription', 'data', 'texts'],
        ['boardSettings', 'extendedDescription', 'data', 'images'],
      ],
      action: () => (board: ClientBoardV2) => this._injector.get(BackendBoardsV2Service).updateMainStory(board),
    },
    createEmissionStory: {
      paths: [['boardSettings', 'emissionsPathways']],
      action: () => (board: ClientBoardV2) => this._injector.get(BackendBoardsV2Service).createEmissionStory(board),
    },
    emissionStory: {
      paths: [
        ['boardSettings', 'emissionsPathways', 'description'],
        ['boardSettings', 'emissionsPathways', 'title'],
        ['boardSettings', 'emissionsPathways', 'imageCredit'],
        ['boardSettings', 'emissionsPathways', 'image'],
      ],
      action: () => (board: ClientBoardV2) => this._injector.get(BackendBoardsV2Service).updateEmissionStory(board),
    },
    createFeedbackSettings: {
      paths: [['boardSettings', 'feedbackSettings']],
      action: () => (board: ClientBoardV2) =>
        this._injector
          .get(BackendBoardsV2Service)
          .createFeedbackSettings(board)
          .pipe(map(() => 'ok')),
    },
    feedbackSettings: {
      paths: [
        ['boardSettings', 'feedbackSettings', 'email'],
        ['boardSettings', 'feedbackSettings', 'organisation'],
        ['boardSettings', 'feedbackSettings', 'privacyNotice'],
        ['boardSettings', 'feedbackSettings', 'privacyPolicyURL'],
      ],
      action: () => (board: ClientBoardV2) =>
        this._injector.get(BackendBoardsV2Service).updateOrDeleteFeedbackSettings(board),
    },
    boardSettings: {
      paths: [
        ['boardSettings', 'timeline', 'boardStartYear'],
        ['boardSettings', 'timeline', 'activityBasedInventoryStartYear'],
        ['boardSettings', 'timeline', 'scenarioStartYear'],
        ['boardSettings', 'timeline', 'latestFinalisedInventoryYear'],
        ['boardSettings', 'timeline', 'boardEndYear'],
        ['boardSettings', 'internationalisation', 'defaultLanguage'],
        ['boardSettings', 'electricityEmissionFactorType'],
      ],
      action: () => (board: ClientBoardV2) =>
        this._injector
          .get(BackendBoardsV2Service)
          .updateApiBoardSettings(board)
          .pipe(
            tap(() => {
              this._injector.get(MultiyearUpdateValuesService).clearState();
              this._injector.get(InventoriesFacadeService).resetInventories();
              this._injector.get(ActionsFacadeService).resetOutcomeAssessmentsState();
            })
          ),
    },
    dashboardSettings: {
      paths: [
        ['boardSettings', 'pointOfEntrySettings', 'header', 'country'],
        ['boardSettings', 'pointOfEntrySettings', 'header', 'tagline'],
        ['boardSettings', 'pointOfEntrySettings', 'header', 'about'],
        ['boardSettings', 'pointOfEntrySettings', 'header', 'taglineSubText'],
        ['boardSettings', 'pointOfEntrySettings', 'header', 'backgroundImageCredits'],
        ['boardSettings', 'pointOfEntrySettings', 'header', 'backgroundImage', 'large'],
        ['boardSettings', 'pointOfEntrySettings', 'header', 'boardTitle'],
        ['boardSettings', 'pointOfEntrySettings', 'footer', 'bodyLeft'],
        ['boardSettings', 'pointOfEntrySettings', 'footer', 'bodyRight'],
        ['boardSettings', 'goalTitle'],
        ['boardSettings', 'introductionSummary'],
        ['boardSettings', 'introductionTitle'],
        ['boardSettings', 'pathwayGap', 'summary'],
        ['boardSettings', 'yearByYearDiagramLayout', 'showBauAndAbatement'],
        ['boardSettings', 'transitionTargetNodes', 'showCardImages'],
        ['boardSettings', 'transitionTargetNodes', 'layout'],
        ['boardSettings', 'defaultActionsView'],
        ['boardSettings', 'showUnconnectedActions'],
        ['boardSettings', 'showActionsCosts'],
        ['boardSettings', 'publicTabs'],
      ],
      action: () => (board: ClientBoardV2) => this._injector.get(BackendBoardsV2Service).updateDashboardSettings(board),
    },
    v3RequiredFields: {
      paths: [
        ['boardSettings', 'doNotDistributeUntargeted'],
        ['boardSettings', 'totalEmissions'],
        ['boardSettings', 'unaddressedEmissionsDisplay'],
        ['boardSettings', 'useNewTargetedEmissions'],
      ],
      action: () => (board: ClientBoardV2) => this._injector.get(BackendBoardsV2Service).updateV3RequiredFields(board),
    },
  };

  public queueBoardForSaving({
    boardId,
    tenantId,
    updates,
    board,
  }: Omit<QueueItem, 'saveType'>): Observable<ClientBoard> {
    const exists = this._saveQueue.has(boardId);
    if (!this._observables.has(boardId)) {
      this._observables.set(boardId, new Subject());
    }
    if (exists) {
      const storedOperation = this._saveQueue.get(boardId);
      this._saveQueue.set(boardId, { ...storedOperation, updates: storedOperation.updates.concat(updates) });
    } else if (this._currentlySaving.has(boardId)) {
      this._saveQueue.set(boardId, {
        board,
        updates,
        boardId,
        tenantId: tenantId,
      });
    } else {
      const updatedBoard = this._getUpdatedBoard(board, updates);
      this._saveBoard(updatedBoard, board, boardId);
    }
    return this._observables.get(boardId);
  }

  private _getBoardWithUpdatedProperty(
    board: ClientBoard,
    property: RecursiveKeyOf<ClientBoard>[],
    value: any
  ): ClientBoard {
    const boardClone = structuredClone(board);
    property.reduce((parentObject, currentProperty, index, properties) => {
      if (index === properties.length - 1) {
        parentObject[currentProperty] = value;
      } else {
        if (parentObject[currentProperty] === undefined || parentObject[currentProperty] === null) {
          parentObject[currentProperty] = {};
        }
        return parentObject[currentProperty];
      }
    }, boardClone);

    return boardClone;
  }

  private _getUpdatedBoard(
    board: ClientBoard,
    updates: { property: RecursiveKeyOf<ClientBoard>[]; value: any }[]
  ): ClientBoard {
    return updates.reduce(
      (board, update) => this._getBoardWithUpdatedProperty(board, update.property, update.value),
      board
    );
  }

  private _markAsCurrentlySaving(boardId: string): void {
    this._currentlySaving.add(boardId);
  }

  private _markAsSaved({ boardId, updatedBoard }: { boardId: string; updatedBoard: ClientBoard }): void {
    this._currentlySaving.delete(boardId);

    if (this._saveQueue.has(boardId)) {
      const nextSaveOperation = this._saveQueue.get(boardId);
      this._saveQueue.delete(boardId);
      const nextUpdatedBoard = this._getUpdatedBoard(updatedBoard, nextSaveOperation.updates);
      this._saveBoard(nextUpdatedBoard, updatedBoard, nextSaveOperation.boardId);
    } else {
      const saveObservable = this._observables.get(boardId);
      saveObservable.next(updatedBoard);
      saveObservable.complete();
      this._observables.delete(boardId);
    }
  }

  private _saveBoard(updatedBoard: ClientBoard, oldBoard: ClientBoard, boardId: string): void {
    this._markAsCurrentlySaving(boardId);
    this._executeActionsIfPropertiesChanged(updatedBoard as ClientBoardV2, oldBoard as ClientBoardV2).subscribe(() => {
      this._markAsSaved({ boardId, updatedBoard });
    });
  }

  private _executeActionsIfPropertiesChanged(board: ClientBoardV2, oldBoard: ClientBoardV2): ExecuteBoardRequests {
    const changedProperties = findDifferentProperties(board, oldBoard);
    let requests: BoardRequests = [
      ...this._executeActionStatusRequestsIfChanged(changedProperties, board, oldBoard),
      ...this._executeActionTagRequestsIfChanged(changedProperties, board, oldBoard),
      ...this._executeActionTypeRequestsIfChanged(changedProperties, board, oldBoard),
      ...this._executeActionOriginRequestsIfChanged(changedProperties, board, oldBoard),
      ...this._executeStakeholderRequestsIfChanged(changedProperties, board, oldBoard),
      ...this._executeFooterLogosRequestIfChanged(changedProperties, board, oldBoard),
    ];

    Object.entries(this._propertyActions).forEach(([_, { paths, action }]) => {
      const hasRelevantChanges = paths.some((path) =>
        changedProperties.some((changedPath) => path.join() === changedPath.join())
      );
      if (hasRelevantChanges) {
        const executeAction = action();
        requests = [...requests, executeAction(board as ClientBoardV2)];
      }
    });

    return requests.length ? forkJoin(requests) : of(['Ok']);
  }

  private _executeActionTagRequestsIfChanged(
    changedProperties: string[][],
    board: ClientBoardV2,
    oldBoard: ClientBoardV2
  ): (Observable<ActionTagResponse> | Observable<string>)[] {
    if (!changedProperties.some((changedPath) => changedPath.join(' - ') === 'boardSettings - actionTags')) {
      return [];
    }

    const actionTags = board.boardSettings.actionTags;
    const oldActionTags = oldBoard.boardSettings.actionTags ?? [];

    const deletedActionTags = oldActionTags.filter((oldTag) => {
      return !actionTags.some((tag) => tag.id === oldTag.id);
    });
    const createdActionTags = actionTags.filter((tag) => {
      return !oldActionTags.some((oldTag) => oldTag.id === tag.id);
    });
    const updatedActionTags = actionTags.filter((tag) => {
      const oldTag = oldActionTags.find((oldTag) => oldTag.id === tag.id);
      if (!oldTag) {
        return false;
      }
      return oldTag.title !== tag.title || oldTag.colour !== tag.colour;
    });

    const backendBoardsV2Service = this._injector.get(BackendBoardsV2Service);

    const oldOrder = oldActionTags.map((status) => status.id);
    const newOrder = actionTags.map((status) => status.id);
    const changedOrder = oldOrder.some((id, index) => id !== newOrder[index]);

    /**
     * Changing order should only be possible if no other create/update/delete happens simultaneously
     */
    const isOnlyChangedOrder =
      changedOrder && !deletedActionTags.length && !createdActionTags.length && !updatedActionTags.length;
    if (isOnlyChangedOrder) {
      const movedActionTagId = findMovedItemId(oldOrder, newOrder);
      if (!movedActionTagId) {
        return [];
      }
      const movedActionTag = actionTags.find((status) => status.id === movedActionTagId);
      if (!movedActionTag) {
        return [];
      }

      const updateParams = {
        boardId: board.id,
        actionTagId: movedActionTag.id,
        language: board.boardSettings.internationalisation.defaultLanguage,
        name: movedActionTag.title,
        sortOrder: newOrder.indexOf(movedActionTag.id),
        colour: movedActionTag.colour,
      };
      return [<Observable<string>>backendBoardsV2Service.updateActionTag(updateParams).pipe(
          catchError((error) => {
            console.error(`Error updating action tag ${movedActionTag.id}:`, error);
            throw error;
          })
        )];
    }

    const deleteRequests = deletedActionTags.map((tag) => {
      return backendBoardsV2Service.deleteActionTag(board.id, tag.id).pipe(
        catchError((error) => {
          console.error(`Error deleting action tag ${tag.id}:`, error);
          throw error;
        })
      );
    });

    const createRequests = createdActionTags.map((tag) => {
      const language = board.boardSettings.internationalisation.defaultLanguage;
      const sortOrder = newOrder.indexOf(tag.id);

      return backendBoardsV2Service
        .createActionTag({ boardId: board.id, language, name: tag.title, sortOrder, tagId: tag.id })
        .pipe(
          catchError((error) => {
            console.error(`Error creating action tag ${tag.id}:`, error);
            throw error;
          })
        );
    });

    const updateRequests = updatedActionTags.map((tag) => {
      const language = board.boardSettings.internationalisation.defaultLanguage;

      return backendBoardsV2Service
        .updateActionTag({
          boardId: board.id,
          actionTagId: tag.id,
          language,
          name: tag.title,
          colour: tag.colour,
          ...(changedOrder ? { sortOrder: newOrder.indexOf(tag.id) } : {}),
        })
        .pipe(
          catchError((error) => {
            console.error(`Error updating action tag ${tag.id}:`, error);
            throw error;
          })
        );
    });

    return [...deleteRequests, ...createRequests, ...updateRequests];
  }

  private _executeActionTypeRequestsIfChanged(
    changedProperties: string[][],
    board: ClientBoardV2,
    oldBoard: ClientBoardV2
  ): (Observable<string> | Observable<ActionTypeResponse>)[] {
    const hasRelevantChanges = changedProperties.some((path) => path.join(' - ') === 'boardSettings - actionTypes');
    if (!hasRelevantChanges) {
      return [];
    }

    const backendBoardsV2Service = this._injector.get(BackendBoardsV2Service);
    const actionTypes = board.boardSettings.actionTypes;
    const oldActionTypes = oldBoard.boardSettings.actionTypes ?? [];
    const {
      created: createdActionTypes,
      updated: updatedActionTypes,
      deleted: deletedActionTypes,
    } = distinguishChangedItems(actionTypes, oldActionTypes);

    const oldOrder = oldActionTypes.map((status) => status.id);
    const newOrder = actionTypes.map((status) => status.id);
    const changedOrder = oldOrder.some((id, index) => id !== newOrder[index]);
    /**
     * Changing order should only be possible when no other create/update/delete happens simultaneously
     */
    const isOnlyChangedOrder =
      changedOrder && !deletedActionTypes.length && !createdActionTypes.length && !updatedActionTypes.length;
    if (isOnlyChangedOrder) {
      const movedActionTypeId = findMovedItemId(oldOrder, newOrder);
      if (!movedActionTypeId) {
        return [];
      }
      const movedActionType = actionTypes.find((type) => type.id === movedActionTypeId);
      if (!movedActionType) {
        return [];
      }

      const updateParams = {
        boardId: board.id,
        language: board.boardSettings.internationalisation.defaultLanguage,
        name: movedActionType.displayValue,
        typeId: movedActionType.id,
        sortOrder: newOrder.indexOf(movedActionType.id),
      };
      return [<Observable<string>>backendBoardsV2Service.updateActionType(updateParams).pipe(
          catchError((error) => {
            console.error(`Error updating action status ${movedActionType.id}:`, error);
            throw error;
          })
        )];
    }

    const deleteRequests = deletedActionTypes.map((actionType) => {
      return backendBoardsV2Service.deleteActionType(board.id, actionType.id).pipe(
        catchError((error) => {
          console.error(`Error deleting actionType ${actionType.id}:`, error);
          throw error;
        })
      );
    });
    const createRequests = createdActionTypes.map((actionType) => {
      const createParams = {
        boardId: board.id,
        language: board.boardSettings.internationalisation.defaultLanguage,
        name: actionType.displayValue,
        typeId: actionType.id,
        sortOrder: newOrder.indexOf(actionType.id),
      };
      return backendBoardsV2Service.createActionType(createParams).pipe(
        catchError((error) => {
          console.error(`Error creating actionType ${actionType.id}:`, error);
          throw error;
        })
      );
    });
    const updateRequests = updatedActionTypes.map((actionType) => {
      const updateParams = {
        boardId: board.id,
        language: board.boardSettings.internationalisation.defaultLanguage,
        name: actionType.displayValue,
        typeId: actionType.id,
        sortOrder: newOrder.indexOf(actionType.id),
        ...(changedOrder ? { sortOrder: newOrder.indexOf(actionType.id) } : {}),
      };
      return backendBoardsV2Service.updateActionType(updateParams).pipe(
        catchError((error) => {
          console.error(`Error updating actionType ${actionType.id}:`, error);
          throw error;
        })
      );
    });

    return [...deleteRequests, ...createRequests, ...updateRequests];
  }

  private _executeActionStatusRequestsIfChanged(
    changedProperties: string[][],
    board: ClientBoardV2,
    oldBoard: ClientBoardV2
  ): (Observable<string> | Observable<ActionStatusResponse>)[] {
    const hasRelevantChanges = changedProperties.some((path) => path.join(' - ') === 'boardSettings - swimlanes');
    if (!hasRelevantChanges) {
      return [];
    }

    const actionStatuses = board.boardSettings.swimlanes;
    const oldActionStatuses = oldBoard.boardSettings.swimlanes ?? [];
    const {
      created: createdActionStatuses,
      updated: updatedActionStatuses,
      deleted: deletedActionStatuses,
    } = distinguishChangedItems(actionStatuses, oldActionStatuses);

    /**
     * Throw error if any of the created or updated action statuses have no type set
     */
    const shouldErrorActionStatuses = [...createdActionStatuses, ...updatedActionStatuses].filter(
      (s) => s.type === ACTION_STATUS.NOTSET
    );
    if (shouldErrorActionStatuses.length) {
      throw new Error(
        `Action status is not set for action id:\n${shouldErrorActionStatuses.map((s) => s.id).join('\n')}`
      );
    }

    const backendBoardsV2Service = this._injector.get(BackendBoardsV2Service);

    const oldOrder = oldActionStatuses.map((status) => status.id);
    const newOrder = actionStatuses.map((status) => status.id);
    const changedOrder = oldOrder.some((id, index) => id !== newOrder[index]);

    /**
     * Changing order should only be possible if no other create/update/delete happens simultaneously
     */
    const isOnlyChangedOrder =
      changedOrder && !deletedActionStatuses.length && !createdActionStatuses.length && !updatedActionStatuses.length;
    if (isOnlyChangedOrder) {
      const movedActionStatusId = findMovedItemId(oldOrder, newOrder);
      if (!movedActionStatusId) {
        return [];
      }
      const movedActionStatus = actionStatuses.find((status) => status.id === movedActionStatusId);
      if (!movedActionStatus) {
        return [];
      }

      const updateParams = {
        boardId: board.id,
        language: board.boardSettings.internationalisation.defaultLanguage,
        name: movedActionStatus.title,
        status: movedActionStatus.type,
        statusId: movedActionStatus.id,
        sortOrder: newOrder.indexOf(movedActionStatusId),
      };
      return [<Observable<string>>backendBoardsV2Service.updateActionStatus(updateParams).pipe(
          catchError((error) => {
            console.error(`Error updating action status ${movedActionStatus.id}:`, error);
            throw error;
          })
        )];
    }

    /**
     * Collect requests for creating, updating and deleting action statuses
     */
    const deleteRequests = deletedActionStatuses.map((status) => {
      return backendBoardsV2Service.deleteActionStatus(board.id, status.id as string).pipe(
        catchError((error) => {
          console.error(`Error deleting action status ${status.id}:`, error);
          throw error;
        })
      );
    });
    const createRequests = createdActionStatuses.map((status) => {
      const createParams = {
        boardId: board.id,
        language: board.boardSettings.internationalisation.defaultLanguage,
        name: status.title,
        status: <ACTION_STATUS_ALLOWED>status.type,
        sortOrder: newOrder.indexOf(status.id),
        statusId: status.id,
      };
      return backendBoardsV2Service.createActionStatus(createParams).pipe(
        catchError((error) => {
          console.error(`Error creating action status ${status.id}:`, error);
          throw error;
        })
      );
    });
    const updateRequests = updatedActionStatuses.map((status) => {
      const updateParams = {
        boardId: board.id,
        language: board.boardSettings.internationalisation.defaultLanguage,
        name: status.title,
        status: status.type,
        statusId: status.id as string,
        ...(changedOrder ? { sortOrder: newOrder.indexOf(status.id) } : {}),
      };
      return backendBoardsV2Service.updateActionStatus(updateParams).pipe(
        catchError((error) => {
          console.error(`Error updating action status ${status.id}:`, error);
          throw error;
        })
      );
    });

    /**
     * Execute all requests
     */
    return [...deleteRequests, ...createRequests, ...updateRequests];
  }

  private _executeFooterLogosRequestIfChanged(
    changedProperties: string[][],
    board: ClientBoardV2,
    oldBoard: ClientBoardV2
  ): (Observable<string> | Observable<LinkingImageResponse>)[] {
    const hasRelevantChanges = changedProperties.some(
      (path) => path.join('.') === 'boardSettings.pointOfEntrySettings.footer.images'
    );
    if (!hasRelevantChanges) {
      return [];
    }

    const logos = board.boardSettings?.pointOfEntrySettings?.footer?.images ?? [];
    const oldLogos = oldBoard.boardSettings?.pointOfEntrySettings?.footer?.images ?? [];

    const created = logos.filter((l) => !oldLogos.some((oL) => oL.id === l.id));
    const deleted = oldLogos.filter((oL) => !logos.some((l) => l.id === oL.id));
    const updated = logos
      .map((l) => {
        const old = oldLogos.find((oL) => oL.id === l.id);
        if (old && (l.sourceURL !== old.sourceURL || l.destinationURL !== old.destinationURL)) {
          return {
            id: l.id as string,
            ...(l.sourceURL !== old.sourceURL ? { sourceUrl: l.sourceURL } : {}),
            ...(l.destinationURL !== old.destinationURL ? { destinationUrl: l.destinationURL } : {}),
          };
        }
        return undefined;
      })
      .filter((u): u is NonNullable<typeof u> => u !== undefined && u !== null);
    const backendBoardsV2Service = this._injector.get(BackendBoardsV2Service);

    const createRequests = created.map((c) =>
      backendBoardsV2Service.createFooterLogo(board.id, {
        id: c.id,
        imageUrl: c.sourceURL,
        ...(c.destinationURL ? { destinationUrl: c.destinationURL } : {}),
      })
    );

    const deleteRequests = deleted.map((d) => backendBoardsV2Service.deleteFooterLogos(board.id, d.id));

    const updateRequests = updated.map((u) => backendBoardsV2Service.updateFooterLogo(board.id, u));

    return [...createRequests, ...deleteRequests, ...updateRequests];
  }

  private _executeStakeholderRequestsIfChanged(
    changedProperties: string[][],
    board: ClientBoardV2,
    oldBoard: ClientBoardV2
  ): (Observable<string> | Observable<StakeholderResponse>)[] {
    const hasRelevantChanges = changedProperties.some((path) => path.join(' - ') === 'boardSettings - stakeholders');
    if (!hasRelevantChanges) {
      return [];
    }

    const backendBoardsV2Service = this._injector.get(BackendBoardsV2Service);
    const stakeholders = board.boardSettings.stakeholders ?? [];
    const oldStakeholders = oldBoard.boardSettings.stakeholders ?? [];
    const {
      created: createdStakeholders,
      updated: updatedStakeholders,
      deleted: deletedStakeholders,
    } = distinguishChangedItems(stakeholders, oldStakeholders);

    const oldOrder = oldStakeholders.map((status) => status.id);
    const newOrder = stakeholders.map((status) => status.id);
    const changedOrder = oldOrder.some((id, index) => id !== newOrder[index]);
    /**
     * Changing order should only be possible when no other create/update/delete happens simultaneously
     */
    const isOnlyChangedOrder =
      changedOrder && !deletedStakeholders.length && !createdStakeholders.length && !updatedStakeholders.length;
    if (isOnlyChangedOrder) {
      const movedActionStatusId = findMovedItemId(oldOrder, newOrder);
      if (!movedActionStatusId) {
        return [];
      }
      const movedStakeholder = stakeholders.find((status) => status.id === movedActionStatusId);
      if (!movedStakeholder) {
        return [];
      }

      if (!isValidStakeholder(movedStakeholder)) {
        throw new Error(`Stakeholder type is not set for stakeholder id:\n${movedStakeholder.id}`);
      }
      const updateParams = {
        boardId: board.id,
        language: board.boardSettings.internationalisation.defaultLanguage,
        name: movedStakeholder.name,
        stakeholder: <STAKEHOLDER_TYPE_ALLOWED>movedStakeholder.type,
        stakeholderId: movedStakeholder.id,
        sortOrder: newOrder.indexOf(movedActionStatusId),
      };
      return [<Observable<string>>backendBoardsV2Service.updateStakeholders(updateParams).pipe(
          catchError((error) => {
            console.error(`Error updating action status ${movedStakeholder.id}:`, error);
            throw error;
          })
        )];
    }

    const { validCreatedStakeholders, validUpdatedStakeholders } = validateCreatedAndUpdatedStakeholders(
      createdStakeholders,
      updatedStakeholders
    );

    const deleteRequests = deletedStakeholders.map((stakeholder) => {
      return backendBoardsV2Service.deleteStakeholder(board.id, stakeholder.id as string).pipe(
        catchError((error) => {
          console.error(`Error deleting stakeholder ${stakeholder.id}:`, error);
          throw error;
        })
      );
    });
    const createRequests = validCreatedStakeholders.map((stakeholder) => {
      const createParams = {
        boardId: board.id,
        language: board.boardSettings.internationalisation.defaultLanguage,
        name: stakeholder.name,
        stakeholder: stakeholder.type,
        sortOrder: newOrder.indexOf(stakeholder.id),
        stakeholderId: stakeholder.id,
      };
      return backendBoardsV2Service.createStakeholder(createParams).pipe(
        catchError((error) => {
          console.error(`Error creating stakeholder ${stakeholder.id}:`, error);
          throw error;
        })
      );
    });
    const updateRequests = validUpdatedStakeholders.map((stakeholder) => {
      const updateParams = {
        stakeholderId: stakeholder.id,
        boardId: board.id,
        name: stakeholder.name,
        stakeholder: stakeholder.type,
        language: board.boardSettings.internationalisation.defaultLanguage,
        ...(changedOrder ? { sortOrder: newOrder.indexOf(stakeholder.id) } : {}),
      };
      return backendBoardsV2Service.updateStakeholders(updateParams).pipe(
        catchError((error) => {
          console.error(`Error updating stakeholder ${stakeholder.id}:`, error);
          throw error;
        })
      );
    });

    return [...deleteRequests, ...createRequests, ...updateRequests];
  }

  private _executeActionOriginRequestsIfChanged(
    changedProperties: string[][],
    board: ClientBoardV2,
    oldBoard: ClientBoardV2
  ): (Observable<string> | Observable<ActionOriginResponse>)[] {
    if (!changedProperties.some((changedPath) => changedPath.join(' - ') === 'boardSettings - actionOrigins')) {
      return [];
    }

    const backendBoardsV2Service = this._injector.get(BackendBoardsV2Service);
    const actionOrigins = board.boardSettings.actionOrigins || [];
    const oldActionOrigins = oldBoard.boardSettings.actionOrigins || [];
    const {
      created: createdActionOrigins,
      updated: updatedActionOrigins,
      deleted: deletedActionOrigins,
    } = distinguishChangedItems<ActionOrigin>(actionOrigins, oldActionOrigins);

    const oldOrder = oldActionOrigins.map((origin) => origin.id);
    const newOrder = actionOrigins.map((origin) => origin.id);
    const changedOrder = oldOrder.some((id, index) => id !== newOrder[index]);

    /**
     * Changing order should only be possible when no other create/update/delete happens simultaneously
     */
    const isOnlyChangedOrder =
      changedOrder && !deletedActionOrigins.length && !createdActionOrigins.length && !updatedActionOrigins.length;
    if (isOnlyChangedOrder) {
      const movedActionOriginId = findMovedItemId(oldOrder, newOrder);
      if (!movedActionOriginId) {
        return [];
      }
      const movedActionOrigin = actionOrigins.find((origin) => origin.id === movedActionOriginId);
      if (!movedActionOrigin) {
        return [];
      }

      const updateParams = {
        boardId: board.id,
        language: board.boardSettings.internationalisation.defaultLanguage,
        name: movedActionOrigin.title,
        originId: movedActionOrigin.id,
        originType: movedActionOrigin.type,
        description: movedActionOrigin.description || '',
        sortOrder: newOrder.indexOf(movedActionOriginId),
      };
      return [<Observable<string>>backendBoardsV2Service.updateActionOrigin(updateParams).pipe(
          catchError((error) => {
            console.error(`Error updating action origin ${movedActionOrigin.id}:`, error);
            throw error;
          })
        )];
    }

    const deleteRequests = deletedActionOrigins.map((origin) => {
      return backendBoardsV2Service.deleteActionOrigin(board.id, origin.id as string).pipe(
        catchError((error) => {
          console.error(`Error deleting action origin ${origin.id}:`, error);
          throw error;
        })
      );
    });

    const createRequests = createdActionOrigins.map((origin) => {
      const createParams = {
        boardId: board.id,
        language: board.boardSettings.internationalisation.defaultLanguage,
        name: origin.title,
        originType: origin.type,
        originId: origin.id,
        description: origin.description || '',
        sortOrder: newOrder.indexOf(origin.id),
      };
      return backendBoardsV2Service.createActionOrigin(createParams).pipe(
        catchError((error) => {
          console.error(`Error creating action origin ${origin.id}:`, error);
          throw error;
        })
      );
    });

    const updateRequests = updatedActionOrigins.map((origin) => {
      const updateParams = {
        boardId: board.id,
        language: board.boardSettings.internationalisation.defaultLanguage,
        name: origin.title,
        originId: origin.id,
        originType: origin.type,
        description: origin.description || '',
        ...(changedOrder ? { sortOrder: newOrder.indexOf(origin.id) } : {}),
      };
      return backendBoardsV2Service.updateActionOrigin(updateParams).pipe(
        catchError((error) => {
          console.error(`Error updating action origin ${origin.id}:`, error);
          throw error;
        })
      );
    });

    return [...deleteRequests, ...createRequests, ...updateRequests];
  }
}
